import {ComponentProps, useState} from "react";
import {DialogContent, FormattedDialogTitle, LoadingIcon} from "@dvrt/graphql-widgets";
import {FormattedFormRow, FormContainer, FormDialogActionButtons} from "@dvrt/graphql-widgets";
import {Checkbox, Dialog, DialogActions, FormControl, FormHelperText, MenuItem, Select, TextField} from "@mui/material";
import MultipleLineError from "../../../../MultipleLineError";
import ErrorHelper from "../../../../../utils/ErrorHelper";
import CreateAnotherCheckBox from "../../../../CreateAnotherCheckBox";
import {FormattedMessage, MessageDescriptor, defineMessage} from "react-intl";
import ProvisionedForm, {createInitialData, DEFAULT_VARIANT_NAME} from "./ProvisionedForm";
import {DataSource} from "./DataSource";
import {commonTranslations} from "@/utils/translations";

type Props = {
    isOpen: boolean;
    close: () => void;
    onSuccess: () => void;
    dataSource: DataSource;
    title: MessageDescriptor;
    titleValues?: ComponentProps<typeof FormattedMessage>["values"];
    cacheKeyPostfix?: string;
};

const LoadedFormContent = ({
    dataSource,
    cacheKeyPostfix,
    close,
    onSuccess,
}: {
    dataSource: DataSource;
    cacheKeyPostfix: string;
    close: () => void;
    onSuccess: () => void;
}) => {
    const [createAnother, setCreateAnother] = useState(false);
    const [success, setSuccess] = useState(false);

    const initialData = createInitialData(dataSource);
    const form = new ProvisionedForm(initialData, cacheKeyPostfix);
    const errorHelper = new ErrorHelper(form.errors);

    async function submit() {
        if (!(await form.submit())) {
            return;
        }

        setSuccess(true);
        if (!form.values.id) form.reset();

        if (!createAnother) handleClose(true);
    }

    function handleClose(asyncSuccess?: boolean) {
        const actualSuccess = asyncSuccess === null ? success : asyncSuccess;

        close();
        if (actualSuccess) onSuccess();
        form.reset();
    }

    function createVendorOptions() {
        return dataSource.domainAdminData.vendors.map((item) => (
            <MenuItem key={item.id} value={item.id} id={`provisioned-device-vendor-${item.name}`}>
                {item.name}
            </MenuItem>
        ));
    }

    function createVariantOptions() {
        const variants = [
            <MenuItem key={DEFAULT_VARIANT_NAME} value={DEFAULT_VARIANT_NAME} id={`provisioned-device-variant-default`}>
                <FormattedMessage id="default" defaultMessage="Default" />
            </MenuItem>,
        ];

        for (const item of dataSource.domainAdminData.variants) {
            variants.push(
                <MenuItem key={item.id} value={item.name} id={`provisioned-device-variant-${item.name}`}>
                    {item.name}
                </MenuItem>,
            );
        }

        return variants;
    }

    function createStationOptions() {
        const items = [];
        items.push(
            <MenuItem key={0} value={0} id="provisioned-device-station-name-use-e164">
                <FormattedMessage id="use_e164" defaultMessage="Use E.164 number" />
            </MenuItem>,
        );
        items.push(
            <MenuItem key={1} value={1} id="provisioned-device-station-name-use-display-name">
                <FormattedMessage id="use_display_name" defaultMessage="Use display name" />
            </MenuItem>,
        );
        return items;
    }

    return (
        <>
            <DialogContent>
                {errorHelper.hasError("limit") && (
                    <p style={{color: "red"}}>
                        <FormattedMessage id="quota_depleted" defaultMessage="Quota depleted" />
                    </p>
                )}
                <FormContainer>
                    <FormattedFormRow
                        label={defineMessage({id: "mac_address", defaultMessage: "MAC address"})}
                        required
                    >
                        <TextField
                            variant="standard"
                            id="provisioned-device-mac-address"
                            margin="dense"
                            value={form.values.macAddress}
                            onChange={(event) => form.updateValues({macAddress: event.target.value})}
                            error={errorHelper.hasError("macAddress")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("macAddress")} />}
                            required
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "vendor", defaultMessage: "Vendor"})}>
                        <FormControl variant="standard" error={errorHelper.hasError("vendorId")}>
                            <Select
                                variant="standard"
                                id="provisioned-device-vendor"
                                value={form.values.vendorId}
                                margin="dense"
                                onChange={(event) => {
                                    form.updateValues({vendorId: Number(event.target.value)});
                                }}
                                error={errorHelper.hasError("vendorId")}
                            >
                                {createVendorOptions()}
                            </Select>
                            <FormHelperText>
                                <MultipleLineError errors={errorHelper.fieldErrors("vendorId")} />
                            </FormHelperText>
                        </FormControl>
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "provisioning", defaultMessage: "Provisioning"})}>
                        <Checkbox
                            id="provisioned-device-provisioning"
                            checked={form.values.provisioning}
                            onChange={(event) => form.updateValues({provisioning: event.target.checked})}
                            color="primary"
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "variant", defaultMessage: "Variant"})}>
                        <FormControl variant="standard" error={errorHelper.hasError("variant")}>
                            <Select
                                variant="standard"
                                id="provisioned-device-variant"
                                value={form.values.variant}
                                margin="dense"
                                onChange={(event) => {
                                    form.updateValues({variant: String(event.target.value)});
                                }}
                                displayEmpty
                            >
                                {createVariantOptions()}
                            </Select>
                            <FormHelperText>
                                <MultipleLineError errors={errorHelper.fieldErrors("variant")} />
                            </FormHelperText>
                        </FormControl>
                    </FormattedFormRow>
                    <FormattedFormRow label={commonTranslations.stationName}>
                        <Select
                            variant="standard"
                            id="provisioned-device-station-name"
                            value={form.values.useDisplayNameForStationName ? 1 : 0}
                            margin="dense"
                            onChange={(event) => {
                                form.updateValues({useDisplayNameForStationName: Boolean(event.target.value)});
                            }}
                        >
                            {createStationOptions()}
                        </Select>
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                {form.values.userId && <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />}
                <FormDialogActionButtons
                    mainId="provisioned-device-save"
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={handleClose}
                    enabled={form.hasChanged}
                />
            </DialogActions>
        </>
    );
};

const FormContent = ({
    dataSource,
    cacheKeyPostfix,
    close,
    onSuccess,
}: {
    dataSource: DataSource;
    cacheKeyPostfix: string;
    close: () => void;
    onSuccess: () => void;
}) => {
    if (dataSource.state === "loading") {
        return (
            <DialogContent>
                <LoadingIcon />
            </DialogContent>
        );
    }

    return (
        <LoadedFormContent
            dataSource={dataSource}
            cacheKeyPostfix={cacheKeyPostfix}
            close={close}
            onSuccess={onSuccess}
        />
    );
};

export default ({isOpen, close, title, dataSource, onSuccess, titleValues, cacheKeyPostfix}: Props) => {
    if (dataSource.state !== "ok") {
        return null;
    }

    return (
        <Dialog open={isOpen}>
            <FormattedDialogTitle title={title} values={titleValues} />
            <FormContent
                dataSource={dataSource}
                cacheKeyPostfix={cacheKeyPostfix}
                close={close}
                onSuccess={onSuccess}
            />
        </Dialog>
    );
};
